import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { 
  Loader2, SlidersHorizontal, ArrowUpDown, ChevronDown, 
  Grid, List, Calendar, CalendarDays, CalendarRange 
} from "lucide-react";
import odooApi from "../services/odooApi";
import { useLanguage } from "../contexts/LanguageContext";
import { useCurrency } from "../contexts/CurrencyContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import CarImage from "../components/CarImage";

const PricingPage = () => {
  const { t } = useLanguage();
  const { formatPrice, currentCurrency } = useCurrency();
  
  const [cars, setCars] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("price_daily_asc");
  const [filterBrand, setFilterBrand] = useState("all");
  const [viewMode, setViewMode] = useState("grid"); // grid or list
  const [priceType, setPriceType] = useState("daily"); // daily, weekly, monthly

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [carsRes, brandsRes] = await Promise.all([
          odooApi.getCars(),
          odooApi.getBrands(),
        ]);
        // Handle both array and {cars: []} response formats
        const carsData = Array.isArray(carsRes) ? carsRes : (carsRes?.cars || []);
        const brandsData = Array.isArray(brandsRes) ? brandsRes : [];
        setCars(carsData);
        setBrands(brandsData);
      } catch (err) {
        console.error("Failed to load pricing data:", err);
        setCars([]);
        setBrands([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Sort and filter cars
  const processedCars = useMemo(() => {
    if (!Array.isArray(cars)) return [];
    let result = [...cars];
    
    // Filter by brand
    if (filterBrand !== "all") {
      result = result.filter(car => car.brandSlug === filterBrand || car.brand?.toLowerCase() === filterBrand.toLowerCase());
    }

    // Sort
    switch (sortBy) {
      case "price_daily_asc":
        result.sort((a, b) => (a.pricePerDay || 0) - (b.pricePerDay || 0));
        break;
      case "price_daily_desc":
        result.sort((a, b) => (b.pricePerDay || 0) - (a.pricePerDay || 0));
        break;
      case "price_weekly_asc":
        result.sort((a, b) => (a.pricePerWeek || a.pricePerDay * 7) - (b.pricePerWeek || b.pricePerDay * 7));
        break;
      case "price_weekly_desc":
        result.sort((a, b) => (b.pricePerWeek || b.pricePerDay * 7) - (a.pricePerWeek || a.pricePerDay * 7));
        break;
      case "price_monthly_asc":
        result.sort((a, b) => (a.pricePerMonth || a.pricePerDay * 30) - (b.pricePerMonth || b.pricePerDay * 30));
        break;
      case "price_monthly_desc":
        result.sort((a, b) => (b.pricePerMonth || b.pricePerDay * 30) - (a.pricePerMonth || a.pricePerDay * 30));
        break;
      case "brand_asc":
        result.sort((a, b) => (a.brand || '').localeCompare(b.brand || ''));
        break;
      case "brand_desc":
        result.sort((a, b) => (b.brand || '').localeCompare(a.brand || ''));
        break;
      default:
        break;
    }

    return result;
  }, [cars, sortBy, filterBrand]);

  const getPrice = (car) => {
    switch (priceType) {
      case "weekly":
        return car.pricePerWeek || car.pricePerDay * 7;
      case "monthly":
        return car.pricePerMonth || car.pricePerDay * 30;
      default:
        return car.pricePerDay;
    }
  };

  const priceLabel = {
    daily: t("common.perDay", "/day"),
    weekly: t("common.perWeek", "/week"),
    monthly: t("common.perMonth", "/month"),
  };

  return (
    <>
      <SEO
        title={t("pricing.pageTitle", "Car Rental Pricing - Velaris Royal")}
        description={t("pricing.pageDescription", "Compare prices for luxury car rentals in Dubai & UAE. Daily, weekly, and monthly rates for all vehicles.")}
      />

      {/* Header */}
      <section className="bg-black py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] font-semibold">
            {t("pricing.subtitle", "Transparent Pricing")}
          </span>
          <h1 className="font-[var(--font-display)] text-3xl sm:text-5xl font-bold text-white mt-3">
            {t("pricing.title", "Car Rental Pricing")}
          </h1>
          <p className="text-white/60 mt-4 max-w-2xl mx-auto">
            {t("pricing.description", "Compare prices across our entire fleet. All prices include basic insurance and VAT.")}
          </p>
        </div>
      </section>

      {/* Filters & Controls */}
      <section className="bg-white dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 sticky top-[80px] z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Price Type Toggle */}
            <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setPriceType("daily")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  priceType === "daily" 
                    ? "bg-[#D4AF37] text-black" 
                    : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                <Calendar className="w-4 h-4" />
                {t("pricing.daily", "Daily")}
              </button>
              <button
                onClick={() => setPriceType("weekly")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  priceType === "weekly" 
                    ? "bg-[#D4AF37] text-black" 
                    : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                <CalendarDays className="w-4 h-4" />
                {t("pricing.weekly", "Weekly")}
              </button>
              <button
                onClick={() => setPriceType("monthly")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  priceType === "monthly" 
                    ? "bg-[#D4AF37] text-black" 
                    : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                <CalendarRange className="w-4 h-4" />
                {t("pricing.monthly", "Monthly")}
              </button>
            </div>

            {/* Brand Filter */}
            <Select value={filterBrand} onValueChange={setFilterBrand}>
              <SelectTrigger className="w-[180px] border-gray-200 dark:border-gray-700">
                <SlidersHorizontal className="w-4 h-4 mr-2 text-gray-400" />
                <SelectValue placeholder={t("pricing.allBrands", "All Brands")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("pricing.allBrands", "All Brands")}</SelectItem>
                {brands.map((brand) => (
                  <SelectItem key={brand.slug} value={brand.slug}>{brand.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[200px] border-gray-200 dark:border-gray-700">
                <ArrowUpDown className="w-4 h-4 mr-2 text-gray-400" />
                <SelectValue placeholder={t("pricing.sortBy", "Sort By")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="price_daily_asc">{t("pricing.priceLowest", "Price: Lowest First")}</SelectItem>
                <SelectItem value="price_daily_desc">{t("pricing.priceHighest", "Price: Highest First")}</SelectItem>
                <SelectItem value="brand_asc">{t("pricing.brandAZ", "Brand: A-Z")}</SelectItem>
                <SelectItem value="brand_desc">{t("pricing.brandZA", "Brand: Z-A")}</SelectItem>
              </SelectContent>
            </Select>

            {/* View Toggle */}
            <div className="flex items-center gap-1 ml-auto">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === "grid" 
                    ? "bg-[#D4AF37] text-black" 
                    : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                }`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === "list" 
                    ? "bg-[#D4AF37] text-black" 
                    : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                }`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>

            {/* Results Count */}
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {processedCars.length} {t("pricing.carsFound", "cars found")}
            </span>
          </div>
        </div>
      </section>

      {/* Pricing Table */}
      <section className="py-8 md:py-12 bg-gray-50 dark:bg-gray-900 min-h-[60vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-10 h-10 animate-spin text-[#D4AF37]" />
            </div>
          ) : viewMode === "grid" ? (
            /* Grid View */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {processedCars.map((car) => (
                <Link
                  key={car.id}
                  to={`/cars/${car.id}`}
                  className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-[#D4AF37] transition-all hover:shadow-lg group"
                  data-testid={`pricing-card-${car.id}`}
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-gray-100 dark:bg-gray-700">
                    <CarImage
                      src={car.image}
                      alt={`${car.brand} ${car.model}`}
                      className="group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute bottom-3 right-3 opacity-60 transition-opacity group-hover:opacity-90">
                      <img
                        src="/velaris-logo.png"
                        alt={t("accessibility.logoAlt", "Velaris Royal Car Rental")}
                        className="h-6 w-auto object-contain drop-shadow-lg"
                        style={{ filter: "brightness(0) invert(1)" }}
                      />
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1 group-hover:text-[#D4AF37] transition-colors">
                      {car.brand} {car.model}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                      {car.year} <span aria-hidden="true">&bull;</span> {car.category}
                    </p>
                    <div className="flex items-baseline justify-between">
                      <span className="text-2xl font-bold text-[#D4AF37]">
                        {formatPrice(getPrice(car))}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {priceLabel[priceType]}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            /* List View */
            <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
              {/* Table Header */}
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700 text-sm font-semibold text-gray-600 dark:text-gray-300">
                <div className="col-span-4">{t("pricing.vehicle", "Vehicle")}</div>
                <div className="col-span-2">{t("pricing.category", "Category")}</div>
                <div className="col-span-2 text-right">{t("pricing.daily", "Daily")}</div>
                <div className="col-span-2 text-right">{t("pricing.weekly", "Weekly")}</div>
                <div className="col-span-2 text-right">{t("pricing.monthly", "Monthly")}</div>
              </div>

              {/* Table Rows */}
              {processedCars.map((car, index) => (
                <Link
                  key={car.id}
                  to={`/cars/${car.id}`}
                  className={`grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors ${
                    index !== processedCars.length - 1 ? "border-b border-gray-100 dark:border-gray-700" : ""
                  }`}
                  data-testid={`pricing-row-${car.id}`}
                >
                  {/* Vehicle */}
                  <div className="col-span-4 flex items-center gap-4">
                    <div className="relative h-14 w-20 overflow-hidden rounded-lg">
                      <CarImage
                        src={car.image}
                        alt={`${car.brand} ${car.model}`}
                        iconClassName="h-5 w-5"
                      />
                      <div className="absolute bottom-1 right-1 opacity-60">
                        <img
                          src="/velaris-logo.png"
                          alt={t("accessibility.logoAlt", "Velaris Royal Car Rental")}
                          className="h-3.5 w-auto object-contain drop-shadow-md"
                          style={{ filter: "brightness(0) invert(1)" }}
                        />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white hover:text-[#D4AF37] transition-colors">
                        {car.brand} {car.model}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{car.year}</p>
                    </div>
                  </div>

                  {/* Category */}
                  <div className="col-span-2">
                    <span className="inline-block px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs font-medium text-gray-600 dark:text-gray-300">
                      {car.category}
                    </span>
                  </div>

                  {/* Daily Price */}
                  <div className="col-span-2 text-right">
                    <span className={`font-semibold ${priceType === 'daily' ? 'text-[#D4AF37] text-lg' : 'text-gray-900 dark:text-white'}`}>
                      {formatPrice(car.pricePerDay)}
                    </span>
                    <span className="md:hidden text-xs text-gray-500 ml-1">/day</span>
                  </div>

                  {/* Weekly Price */}
                  <div className="col-span-2 text-right">
                    <span className={`font-semibold ${priceType === 'weekly' ? 'text-[#D4AF37] text-lg' : 'text-gray-900 dark:text-white'}`}>
                      {formatPrice(car.pricePerWeek || car.pricePerDay * 7)}
                    </span>
                    <span className="md:hidden text-xs text-gray-500 ml-1">/week</span>
                  </div>

                  {/* Monthly Price */}
                  <div className="col-span-2 text-right">
                    <span className={`font-semibold ${priceType === 'monthly' ? 'text-[#D4AF37] text-lg' : 'text-gray-900 dark:text-white'}`}>
                      {formatPrice(car.pricePerMonth || car.pricePerDay * 30)}
                    </span>
                    <span className="md:hidden text-xs text-gray-500 ml-1">/month</span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* No Results */}
          {!loading && processedCars.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-500 dark:text-gray-400 text-lg">
                {t("pricing.noResults", "No cars found matching your criteria.")}
              </p>
              <button
                onClick={() => { setFilterBrand("all"); setSortBy("price_daily_asc"); }}
                className="mt-4 text-[#D4AF37] hover:text-[#C5A028] font-medium"
              >
                {t("pricing.resetFilters", "Reset Filters")}
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default PricingPage;
